// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React, { useState, useEffect, useMemo } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useKeycloak } from "../../context/KeycloakProvider";
import { useRoutes } from "../../hooks/useRoutes";
import { usePermissions } from "../../hooks/usePermissions";
import { userService } from "../../services/userService";
import {
  Home,
  Users,
  Shield,
  Settings,
  User,
  BarChart3,
  X,
  Key,
  LogOut,
  Calendar,
  Languages,
  ClipboardCheck,
  FileText,
  Zap,
  Palette,
  ChevronDown,
  Package,
  LayoutDashboard,
  MapPin,
  Building2,
  Map,
  DollarSign,
  CircleGauge,
  ChartArea,
  ChartPie,
  Menu,
  Phone,
  Moon,
  Sun,
  Bell,
  Clock,
} from "lucide-react";
import toast from "react-hot-toast";
import { hasAdminAccess, hasSuperAdminAccess } from "../../config/roleConfig";
import useTenantBranding from "../../hooks/useTenantBranding";
import { useTheme } from "../../context/ThemeContext";
import { useTranslation } from "react-i18next";

const Sidebar = ({ isOpen, onToggle }) => {
  const { logout, authenticated, getUserInfo } = useKeycloak();
  const { userRoles, getNavigationRoutes } = useRoutes();
  const { hasAnyPermission } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const userInfo = getUserInfo();
  const [profileImageUrl, setProfileImageUrl] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    admin: true,
    user: false,
  });
  const [expandedMenuItems, setExpandedMenuItems] = useState({});
  const { isDarkMode, toggleTheme } = useTheme();

  // Tenant-specific branding (logo, colors)
  const { companyName, logoUrl } = useTenantBranding();
  const { t } = useTranslation();

  if (!authenticated) {
    return null;
  }

  const navigationRoutes = getNavigationRoutes();
  const adminGroups = useMemo(() => {
    const groups = {};
    navigationRoutes.forEach((route) => {
      if (!route.path?.startsWith("/admin")) return;
      if (!route.title) return;
      const groupName = route.name ?? null;
      if (!groups[groupName]) groups[groupName] = [];
      groups[groupName].push(route);
    });
    return groups;
  }, [navigationRoutes]);

  // Memoize the navigation array to prevent infinite re-renders
  const navigation = useMemo(
    () => [
      {
        section: "admin",
        items: Object.entries(adminGroups).flatMap(([groupName, routes]) => {
          const groupItems = routes
            .filter((route) => {
              if (!route.title) return false;
              if (route.permissions && !hasAnyPermission(route.permissions)) {
                return false;
              }
              if (
                route.path.includes("/reports/") &&
                route.path !== "/admin/reports"
              ) {
                return false;
              }
              return true;
            })
            .map((route) => ({
              name: route.title,
              labelKey: route.titleKey,
              href: route.path,
              icon: route.icon || getIconForRoute(route.path),
              children: route.children
                ? route.children
                    .filter((childRoute) => {
                      if (!childRoute.title) return false;
                      if (
                        childRoute.permissions &&
                        !hasAnyPermission(childRoute.permissions)
                      ) {
                        return false;
                      }
                      return true;
                    })
                    .map((childRoute) => {
                      const childPath = childRoute.path.startsWith("/")
                        ? childRoute.path
                        : `${route.path}/${childRoute.path}`;
                      return {
                        name: childRoute.title,
                        labelKey: childRoute.titleKey,
                        href: childPath,
                        icon: childRoute.icon || getIconForRoute(childPath),
                      };
                    })
                : null,
            }));
          if (!groupName || groupName === "null") return groupItems;
          const groupHeader = {
            name: groupName,
            href: "#",
            icon: () => null,
            children: null,
            _isGroupHeader: true,
          };
          return [groupHeader, ...groupItems];
        }),
      },
      {
        section: "user",
        title: t("sidebar.myPanel"),
        items: navigationRoutes
          .filter((route) => {
            if (!route.path.startsWith("/user") || !route.title) return false;
            if (route.permissions && !hasAnyPermission(route.permissions)) {
              return false;
            }
            return true;
          })
          .map((route) => ({
            name: route.title,
            href: route.path,
            icon: route.icon || getIconForRoute(route.path),
          })),
      },
    ],
    [navigationRoutes, hasAnyPermission],
  );

  // Auto-expand: open menu for current route only (accordion); default open first menu with children
  useEffect(() => {
    const currentPath = location.pathname;
    const toExpand = {};
    let firstMenuWithChildren = null;

    navigation.forEach((section) => {
      section.items.forEach((item) => {
        if (item._isGroupHeader) return;
        if (item.children && item.children.length > 0) {
          if (!firstMenuWithChildren) firstMenuWithChildren = item.name;
          const isOnChildRoute = item.children.some(
            (child) => currentPath === child.href,
          );
          const isOnParentRoute = currentPath === item.href;
          if (isOnChildRoute || isOnParentRoute) toExpand[item.name] = true;
        }
      });
    });
    if (Object.keys(toExpand).length > 0) {
      setExpandedMenuItems(toExpand);
    } else if (firstMenuWithChildren) {
      setExpandedMenuItems({ [firstMenuWithChildren]: true });
    }
  }, [location.pathname, navigation]);

  function getIconForRoute(path) {
    const iconMap = {
      "/admin/dashboard": LayoutDashboard,
      "/admin/bookings": Calendar,
      "/admin/dashboard/bookings": Calendar,
      "/admin/business-metrics": Building2,
      "/admin/performance": CircleGauge,
      "/admin/llm-usage": ChartPie,
      "/admin/api-analytics": ChartArea,
      "/admin/workflow-status": Zap,
      "/admin/workflow-voice-settings": Phone,
      "/admin/user-groups": Users,
      "/admin/reports": BarChart3,
      "/admin/financial-forecast": DollarSign,
      "/admin/dashboard/business-metrics": Building2,
      "/admin/dashboard/performance": CircleGauge,
      "/admin/dashboard/llm-usage": ChartPie,
      "/admin/dashboard/api-analytics": ChartArea,
      "/admin/compliance-settings": Settings,
      "/admin/products": Package,
      "/admin/scenarios": MapPin,
      "/admin/availability-view": Clock,
      "/admin/scenario-management": MapPin,
      "/admin/mandanten-management": Building2,
      "/admin/standorte": Map,
      "/admin/standorte-uebersicht": MapPin,
      "/user/dashboard": Home,
      "/user/bookings": Calendar,
      "/user/profile": User,
      "/user/documents": FileText,
      "/admin/users": Users,
      "/admin/roles": Shield,
      "/admin/permissions": Key,
      "/admin/branding-kontakt": Palette,
      "/admin/buchungen-settings": ClipboardCheck,
      "/admin/lokalisierung-settings": Languages,
      "/admin/automation": Zap,
    };
    return iconMap[path] || Settings;
  }

  // Fetch user's profile image and set as blob URL
  useEffect(() => {
    let isCancelled = false;
    let objectUrl = null;
    async function fetchProfileImage() {
      if (!userInfo?.id) return;
      const blob = await userService.getUserPicture(userInfo.id);
      if (isCancelled) return;
      if (blob && blob.size > 0 && blob.type.startsWith("image/")) {
        objectUrl = URL.createObjectURL(blob);
        setProfileImageUrl(objectUrl);
      } else {
        setProfileImageUrl(null);
      }
    }
    fetchProfileImage();
    return () => {
      isCancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [userInfo?.id]);

  const handleLogout = () => {
    logout();
    toast.success(t("sidebar.logoutSuccess"));
    navigate("/login");
  };

  const toggleSection = (section) => {
    if (section !== "admin") {
      setExpandedSections((prev) => ({
        ...prev,
        [section]: !prev[section],
      }));
    }
  };

  const toggleMenuItem = (itemName) => {
    setExpandedMenuItems((prev) => {
      const isOpen = !!prev[itemName];
      return isOpen ? { ...prev, [itemName]: false } : { [itemName]: true };
    });
  };

  // Handle parent menu click - expand dropdown (accordion: only one open at a time)
  const handleParentClick = (item) => {
    if (item.children && item.children.length > 0) {
      setExpandedMenuItems({ [item.name]: true });
    } else {
      setExpandedMenuItems({});
      if (window.innerWidth < 1024) {
        onToggle();
      }
    }
  };

  // Check if parent or any child is active
  const isParentOrChildActive = (item) => {
    const currentPath = location.pathname;
    if (currentPath === item.href) return true;
    if (item.children) {
      return item.children.some((child) => currentPath === child.href);
    }
    return false;
  };

  // Child is active when its path matches or when we're on parent path (treat first child as active)
  const isChildActive = (item, child, index) => {
    const currentPath = location.pathname;
    if (currentPath === child.href) return true;
    if (currentPath === item.href && index === 0) return true;
    return false;
  };

  const isAdmin = hasAdminAccess(userRoles);
  const isUser = userRoles.includes("user");

  const isSuperAdmin = hasSuperAdminAccess(userRoles || []);

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onToggle}
        />
      )}

      <div className="sticky top-0 z-50 lg:hidden bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm">
        <div className="flex items-center justify-between p-3 flex-shrink-0">
          <div
            className="flex items-center"
            style={{ maxWidth: "200px", width: "100%" }}
          >
            <img
              src={
                !isSuperAdmin && logoUrl ? logoUrl : "/logo_b_stadtplatz.svg"
              }
              alt={companyName || "Logo"}
              className="max-h-10 w-auto object-contain dark:invert"
            />
          </div>
          <button
            onClick={onToggle}
            className="lg:hidden p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
          >
            <Menu className="h-6 w-6" />
          </button>
        </div>
      </div>

      <div
        className={`
        fixed top-0 left-0 h-screen z-50 transform transition-transform duration-300 ease-in-out
        bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 shadow-xl
        ${isOpen ? "translate-x-0 w-[280px]" : "-translate-x-full"}
        lg:translate-x-0 lg:z-auto
        w-64 flex flex-col
      `}
      >
        {/* Scoped scrollbar styles for sidebar only */}
        <style>{`
          .sidebar-scroll { scrollbar-width: thin; scrollbar-color: #cbd5e1 transparent; }
          .sidebar-scroll::-webkit-scrollbar { width: 6px; }
          .sidebar-scroll::-webkit-scrollbar-track { background: #f8fafc; border-radius: 3px; }
          .sidebar-scroll::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
          .sidebar-scroll:hover::-webkit-scrollbar-thumb { background: #94a3b8; }
        `}</style>
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex-shrink-0 bg-gray-50/80 dark:bg-gray-800/90">
          <div
            className="flex items-center"
            style={{ maxWidth: "200px", width: "100%" }}
          >
            <img
              src={
                !isSuperAdmin && logoUrl ? logoUrl : "/logo_b_stadtplatz.svg"
              }
              alt={companyName || "Logo"}
              className="max-h-10 w-auto object-contain dark:invert"
            />
          </div>
          <button
            onClick={onToggle}
            className="lg:hidden p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-200 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* User Profile */}
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex-shrink-0 bg-gray-50/50 dark:bg-gray-800/50">
          <div className="flex items-center gap-3">
            <div
              className="rounded-full flex items-center justify-center bg-gradient-primary-secondary-tint ring-2"
              style={{ minWidth: "44px", height: "44px" }}
            >
              {profileImageUrl ? (
                <img
                  src={profileImageUrl}
                  alt="Profile"
                  className="h-11 w-11 rounded-full object-cover"
                />
              ) : (
                <User className="h-6 w-6 primary-color" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 truncate capitalize">
                {userInfo?.firstName
                  ? `${userInfo.firstName} ${userInfo.lastName}`
                  : userInfo?.username || "User"}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {userRoles.join(", ") || t("sidebar.rolesFallback")}
              </p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="sidebar-scroll flex-1 overflow-y-auto py-3 px-2">
          <div className="space-y-4">
            {navigation.map((section) => {
              const shouldShowSection =
                section.section === "admin"
                  ? isAdmin
                  : section.section === "user" && isUser;

              if (!shouldShowSection || section.items.length === 0) return null;

              return (
                <div key={section.section} className="space-y-1">
                  {section.section === "admin" ? null : (
                    <button
                      onClick={() => toggleSection(section.section)}
                      className="w-full flex items-center justify-between px-3 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors border-b border-gray-100 dark:border-gray-700"
                    >
                      <span>{section.title}</span>
                      <ChevronDown
                        className={`h-4 w-4 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${
                          expandedSections[section.section] ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                  )}

                  {expandedSections[section.section] && (
                    <div>
                      {section.items.map((item) => (
                        <div
                          key={
                            item._isGroupHeader
                              ? `header-${item.name ?? "ungrouped"}`
                              : item.name
                          }
                          className="group"
                        >
                          {/* Parent Menu Item */}
                          {item._isGroupHeader ? (
                            item.name ? (
                              <div className="px-3 pt-4 pb-1.5">
                                <h3 className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                                  {item.name}
                                </h3>
                              </div>
                            ) : null
                          ) : null}
                          {!item._isGroupHeader && (
                            <div className="relative mb-0.5">
                              <NavLink
                                to={item.href}
                                onClick={() => handleParentClick(item)}
                                className={({ isActive: _isActive }) => {
                                  const isActiveParent =
                                    isParentOrChildActive(item);
                                  return `flex items-center px-3 py-2.5 text-sm rounded-lg transition-all duration-200 ${
                                    isActiveParent
                                      ? "bg-gradient-primary-secondary-tint primary-color shadow-sm font-medium"
                                      : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100"
                                  }`;
                                }}
                              >
                                <item.icon
                                  className={`mr-3 h-5 w-5 shrink-0 transition-colors ${
                                    isParentOrChildActive(item)
                                      ? "primary-color"
                                      : "text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-200"
                                  }`}
                                />
                                <span className="flex-1 font-medium">
                                  {t(item.labelKey || item.name)}
                                </span>
                                {item.children && item.children.length > 0 && (
                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      toggleMenuItem(item.name);
                                    }}
                                    className={`p-1 rounded-md transition-colors ${
                                      isParentOrChildActive(item)
                                        ? "primary-color hover:bg-primary-color-tint"
                                        : "text-gray-400 dark:text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-600 hover:text-gray-600 dark:hover:text-gray-200"
                                    }`}
                                  >
                                    <ChevronDown
                                      className={`h-4 w-4 transition-transform duration-200 ${
                                        expandedMenuItems[item.name]
                                          ? "rotate-180"
                                          : ""
                                      }`}
                                    />
                                  </button>
                                )}
                              </NavLink>
                            </div>
                          )}

                          {/* Submenu with smooth animation */}
                          {item.children && item.children.length > 0 && (
                            <div
                              className={`mb-2 overflow-hidden transition-all duration-300 ease-in-out ${
                                expandedMenuItems[item.name]
                                  ? "max-h-96 opacity-100"
                                  : "max-h-0 opacity-0"
                              }`}
                            >
                              <div className="ml-4 mt-0.5 space-y-0.5 border-l-2 border-gray-100 dark:border-gray-700 pl-3">
                                {item.children.map((child, index) => {
                                  const childActive = isChildActive(
                                    item,
                                    child,
                                    index,
                                  );
                                  return (
                                    <NavLink
                                      key={child.href}
                                      to={child.href}
                                      onClick={() => {
                                        if (window.innerWidth < 1024)
                                          onToggle();
                                      }}
                                      className={() =>
                                        `flex items-center px-2.5 py-2 text-sm rounded-lg transition-all duration-200 ${
                                          childActive
                                            ? "bg-gradient-primary-secondary-tint primary-color font-medium"
                                            : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-800 dark:hover:text-gray-200"
                                        }`
                                      }
                                      style={{
                                        animationDelay: `${index * 50}ms`,
                                        animation: expandedMenuItems[item.name]
                                          ? "slideInFromLeft 0.3s ease-out forwards"
                                          : "none",
                                      }}
                                    >
                                      <child.icon
                                        className={`mr-2.5 h-4 w-4 shrink-0 ${
                                          childActive
                                            ? "primary-color"
                                            : "text-gray-400"
                                        }`}
                                      />
                                      {t(child.labelKey || child.name)}
                                    </NavLink>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </nav>

        {/* Theme toggle, notification, logout */}
        <div className="flex-shrink-0 flex items-center justify-between px-3 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-800/90">
          <button
            type="button"
            onClick={toggleTheme}
            className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-200 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-600 transition-colors"
            aria-label={
              isDarkMode ? "Switch to light mode" : "Switch to dark mode"
            }
          >
            {isDarkMode ? (
              <Sun className="h-5 w-5" />
            ) : (
              <Moon className="h-5 w-5" />
            )}
          </button>
          <div className="flex items-center gap-0.5">
            <button
              type="button"
              className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-200 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-600 transition-colors"
              aria-label="Notifications"
            >
              <Bell className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={handleLogout}
              className="p-2 rounded-lg text-red-500 hover:text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/30 transition-colors"
              aria-label="Logout"
            >
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
